import { Flex, Avatar, Badge } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { getEmployeeActivities } from "../../api/employeeApi";
import dayjs from "dayjs";
import styled from "styled-components";
import "dayjs/locale/tr";
import CustomDatePicker from "../common/Inputs/CustomDatePicker";
import CustomSelect from "../common/Inputs/CustomSelect";
import DataTable, { Column } from "../common/Table/DataTable";
import { media } from "../../theme";
dayjs.locale("tr");

/**
 * Personelin giriş/çıkış hareketleri.
 *
 * `DataTable`'a geçti: kendi tablosu + kutunun dışına `position: absolute` ile
 * asılmış sayfalama vardı. Tarih ve Giriş/Çıkış süzgeçleri DataTable'ın
 * `toolbar` yuvasına taşındı — dar ekranda alt alta inip tam genişlik oluyorlar.
 * Sıralama oku, Çalışanlar tablosundaki gibi başlık metninde (↑/↓): `Column.header`
 * düz metin, sırf bu ekran için ReactNode'a genişletmeye değmez.
 */

const EmployeeDetailTable = () => {
  const { id } = useParams();
  const [tableData, setTableData] = useState<any>();

  const navigate = useNavigate();
  //for pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<any>(
    window.devicePixelRatio === 1.25 ? "4" : "7"
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  //for sort
  const [sort, setSort] = useState<any>("");
  //for select
  const [selectedActivity, setSelectedActivity] = useState<any>("");
  //for date
  const [formattedSelectedDate, setFormattedSelectedDate] = useState<any>("");
  const [selectedDate, setSelectedDate] = useState<any>();
  //dateinput ref

  const getTableData = async () => {
    try {
      const { data }: any = await getEmployeeActivities({
        pageNumber: page,
        limit: limit,
        sort,
        date: formattedSelectedDate,
        activity: selectedActivity,
        id: id,
      });
      setTableData(data?.data);
      setPage(data?.meta?.page);
      setLimit(data?.meta?.limit);
      setTotalPages(data?.meta?.totalPages);
      setTotalItems(data?.meta?.total);
    } catch (error) {}
  };

  useEffect(() => {
    if (page) {
      getTableData();
    }
  }, [page, sort, selectedActivity, formattedSelectedDate]);

  const columns: Column[] = [
    {
      key: "action",
      header: "Giriş/Çıkış",
      width: "25%",
      render: (row: any) => (
        <Flex align="center" gap={16}>
          <Badge bg={row.action === 1 ? "#DBFFE5" : "#FFE8E9"} size="40px" circle>
            <Avatar
              src={row.action === 1 ? "/assets/entryIcon.png" : "/assets/exitIcon.png"}
              size={24}
            />
          </Badge>
          {row.action === 1 ? "Giriş" : "Çıkış"}
        </Flex>
      ),
    },
    {
      key: "time",
      header: "Saat",
      width: "25%",
      render: (row: any) =>
        // Yerel BELİRTİLMEZSE tarayıcının yereli kullanılır; en-US tarayıcıda
        // "12:44 PM" çıkıyordu. Panelin her yerinde tr-TR + 24 saat.
        new Date(row.date as Date).toLocaleTimeString("tr-TR", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
    },
    {
      key: "date",
      header: `Tarih ${sort === "desc" ? "↓" : "↑"}`,
      width: "25%",
      onHeaderClick: () =>
        setSort((prev: string) => (prev === "desc" ? "asc" : "desc")),
      render: (row: any) => new Date(row.date).toLocaleDateString(),
    },
    { key: "terminalCode", header: "Tablet Kodu", width: "25%" },
  ];

  return (
    <Wrapper>
      <SectionTitle>Çalışan Giriş Çıkış Detayları</SectionTitle>

      <DataTable
        columns={columns}
        rows={tableData ?? []}
        rowKey={(row: any) => row._id}
        emptyText="Bu personel için hareket kaydı yok."
        toolbar={
          <>
            <CustomDatePicker
              placeholder="Tarih Seçiniz"
              onChange={(e: any) => {
                setSelectedDate(e);
                setFormattedSelectedDate(e ? dayjs(e).format("YYYY-MM-DD") : "");
              }}
            />
            <CustomSelect
              placeholder="Giriş/Çıkış"
              data={["Giriş", "Çıkış"]}
              onChange={(e: any) => {
                setSelectedActivity(
                  e === "Giriş" ? 1 : e === "Çıkış" ? 2 : undefined
                );
                setPage(1);
              }}
            />
          </>
        }
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </Wrapper>
  );
};

export default EmployeeDetailTable;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: black;
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 20px;

  ${media.mobile} {
    font-size: 18px;
    margin-bottom: 14px;
  }
`;
